import { useEffect, useMemo, useState } from "react";
import { getAdminProfile } from "../../features/adminAuth/adminAuthStorage";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api.verixxon.com";

const normalizeBaseUrl = (value) => String(value || "").trim().replace(/\/+$/, "");

const getBackendOrigin = (baseUrl) => normalizeBaseUrl(baseUrl).replace(/\/api$/i, "");

const resolveProofImageUrl = (proofImagePath) => {
  const rawPath = String(proofImagePath || "").trim();
  if (!rawPath) {
    return "";
  }

  // Already absolute URL
  if (/^https?:\/\//i.test(rawPath)) {
    return rawPath;
  }

  const normalizedPath = rawPath.startsWith("/") ? rawPath : `/${rawPath}`;
  return `${getBackendOrigin(API_BASE_URL)}${normalizedPath}`;
};

const TRANSACTION_TYPE_LABELS = {
  wallet_topup: "Wallet Top-up",
  otp_purchase: "OTP Purchase",
  otp_refund: "OTP Refund",
  admin_adjustment: "Admin Adjustment",
};

const formatMoney = (value) => `$${Number(value || 0).toFixed(2)}`;

const formatDateTime = (value) => {
  if (!value) return "N/A";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "N/A";
  return parsed.toLocaleString();
};

const TransactionsPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [totals, setTotals] = useState({
    total: 0,
    credits: 0,
    debits: 0,
    creditVolume: 0,
    debitVolume: 0,
    netFlow: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [directionFilter, setDirectionFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [deletingId, setDeletingId] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const [deleteSuccessMsg, setDeleteSuccessMsg] = useState("");
  const [approveSuccessMsg, setApproveSuccessMsg] = useState("");

  const loadTransactions = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/orders/transactions`);
      const payload = await response.json();

      if (!response.ok) {
        setError(payload.message || "Failed to load transactions.");
        return;
      }

      setTransactions(Array.isArray(payload.data) ? payload.data : []);
      setTotals(payload.totals || {
        total: 0,
        credits: 0,
        debits: 0,
        creditVolume: 0,
        debitVolume: 0,
        netFlow: 0,
      });
    } catch (_error) {
      setError("Unable to connect to backend service.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTransaction = async (transactionId, userEmail) => {
    if (!window.confirm(`Delete transaction for ${userEmail}? This action cannot be undone.`)) {
      return;
    }

    setDeletingId(transactionId);
    setError("");
    setDeleteSuccessMsg("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/orders/transactions/${transactionId}`, {
        method: "DELETE",
      });

      const payload = await response.json();

      if (!response.ok) {
        setError(payload.message || "Failed to delete transaction.");
        return;
      }

      setTransactions((prev) => prev.filter((t) => t.id !== transactionId));
      setDeleteSuccessMsg("Transaction deleted successfully.");
      
      // Reload totals
      loadTransactions();
      
      // Clear success message after 3 seconds
      setTimeout(() => setDeleteSuccessMsg(""), 3000);
    } catch (_error) {
      setError("Unable to delete transaction.");
    } finally {
      setDeletingId(null);
    }
  };

  const canApproveManually = (transaction) => {
    return (
      transaction?.status === "pending" &&
      transaction?.category === "wallet_topup" &&
      transaction?.direction === "credit"
    );
  };

  const handleApproveTransaction = async (transaction) => {
    if (!canApproveManually(transaction)) {
      return;
    }

    const approved = window.confirm(
      `Approve pending deposit for ${transaction.userEmail}? This will credit ${formatMoney(transaction.amount)} to the user's wallet.`
    );

    if (!approved) {
      return;
    }

    setApprovingId(transaction.id);
    setError("");
    setDeleteSuccessMsg("");
    setApproveSuccessMsg("");

    try {
      const adminProfile = getAdminProfile();
      const response = await fetch(`${API_BASE_URL}/api/admin/orders/transactions/${transaction.id}/approve`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ adminEmail: adminProfile?.email || "" }),
      });

      const payload = await response.json();

      if (!response.ok) {
        setError(payload.message || "Failed to approve pending deposit.");
        return;
      }

      setApproveSuccessMsg(payload.message || "Pending deposit approved successfully.");
      await loadTransactions();
      setTimeout(() => setApproveSuccessMsg(""), 3000);
    } catch (_error) {
      setError("Unable to approve pending deposit.");
    } finally {
      setApprovingId(null);
    }
  };

  useEffect(() => {
    loadTransactions();
  }, []);

  const categories = useMemo(() => {
    return Array.from(new Set(transactions.map((transaction) => transaction.category).filter(Boolean)));
  }, [transactions]);

  const filteredTransactions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return transactions.filter((transaction) => {
      if (directionFilter !== "all" && transaction.direction !== directionFilter) {
        return false;
      }

      if (categoryFilter !== "all" && transaction.category !== categoryFilter) {
        return false;
      }

      if (!normalizedQuery) {
        return true;
      }

      const haystack = [
        transaction.userEmail,
        transaction.description,
          transaction.gatewayLabel,
        transaction.reference,
        transaction.provider,
        transaction.category,
        transaction.status,
      ]
        .map((item) => String(item || "").toLowerCase())
        .join(" ");

      return haystack.includes(normalizedQuery);
    });
  }, [transactions, query, directionFilter, categoryFilter]);

  return (
    <section className="admin-page-card">
      <h2>Transactions</h2>
      <p>Monitor every wallet credit, debit, refund, and funding flow from one reconciliation view.</p>

      <div className="transactions-summary-grid">
        <article className="transactions-summary-card">
          <span>Total Entries</span>
          <strong>{totals.total || 0}</strong>
        </article>
        <article className="transactions-summary-card">
          <span>Credits</span>
          <strong>{totals.credits || 0}</strong>
        </article>
        <article className="transactions-summary-card">
          <span>Debits</span>
          <strong>{totals.debits || 0}</strong>
        </article>
        <article className="transactions-summary-card">
          <span>Credit Volume</span>
          <strong>{formatMoney(totals.creditVolume || 0)}</strong>
        </article>
        <article className="transactions-summary-card">
          <span>Debit Volume</span>
          <strong>{formatMoney(totals.debitVolume || 0)}</strong>
        </article>
        <article className="transactions-summary-card transactions-summary-card-highlight">
          <span>Net Flow</span>
          <strong>{formatMoney(totals.netFlow || 0)}</strong>
        </article>
      </div>

      <section className="transactions-panel" aria-label="Transactions controls and listing">
        <div className="transactions-panel-header">
          <div>
            <h3>Financial Ledger</h3>
            <p>Search by email, reference, provider, or description and isolate any movement instantly.</p>
          </div>
          <button type="button" className="row-btn row-btn-muted" onClick={loadTransactions}>
            Refresh
          </button>
        </div>

        <div className="transactions-filters">
          <input
            type="text"
            className="transactions-search-input"
            placeholder="Search by email, reference, provider, description"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <select
            className="transactions-filter-select"
            value={directionFilter}
            onChange={(event) => setDirectionFilter(event.target.value)}
          >
            <option value="all">All directions</option>
            <option value="credit">credit</option>
            <option value="debit">debit</option>
          </select>
          <select
            className="transactions-filter-select"
            value={categoryFilter}
            onChange={(event) => setCategoryFilter(event.target.value)}
          >
            <option value="all">All categories</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {TRANSACTION_TYPE_LABELS[category] || category}
              </option>
            ))}
          </select>
        </div>

        {error ? <p className="auth-error">{error}</p> : null}
        {deleteSuccessMsg ? <p className="auth-success">{deleteSuccessMsg}</p> : null}
        {approveSuccessMsg ? <p className="auth-success">{approveSuccessMsg}</p> : null}

        <div className="transactions-table-wrap">
          <table className="transactions-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Type</th>
                <th>Description</th>
                <th>Gateway</th>
                <th>Gateway Reference</th>
                <th>Status</th>
                <th>Amount</th>
                <th>Balance Before</th>
                <th>Balance After</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="11" className="orders-empty-row">Loading transactions...</td>
                </tr>
              ) : filteredTransactions.length === 0 ? (
                <tr>
                  <td colSpan="11" className="orders-empty-row">No transactions found for the current filters.</td>
                </tr>
              ) : (
                filteredTransactions.map((transaction) => (
                  <tr key={transaction.id}>
                    <td>
                      <div className="transactions-user-cell">
                        <strong>{transaction.userEmail || "Unknown user"}</strong>
                        <span>{transaction.userId || "N/A"}</span>
                      </div>
                    </td>
                    <td>
                      <div className="transactions-type-cell">
                        <span
                          className={
                            transaction.direction === "credit"
                              ? "transactions-direction-chip transactions-direction-credit"
                              : "transactions-direction-chip transactions-direction-debit"
                          }
                        >
                          {transaction.direction || "debit"}
                        </span>
                        <small>{TRANSACTION_TYPE_LABELS[transaction.category] || transaction.category || "Transaction"}</small>
                      </div>
                    </td>
                    <td>{transaction.description || "N/A"}</td>
                    <td>
                      <div className="transactions-gateway-cell">
                        <strong>{transaction.gatewayLabel || transaction.provider || "N/A"}</strong>
                        <span>{transaction.gatewayCode || transaction.provider || "N/A"}</span>
                      </div>
                    </td>
                    <td>
                      {transaction.reference || "N/A"}
                      {transaction.proofImagePath ? (
                        <div style={{ marginTop: "6px" }}>
                          <a href={resolveProofImageUrl(transaction.proofImagePath)} target="_blank" rel="noreferrer">
                            View proof
                          </a>
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <span className="transactions-status-badge">{transaction.status || "completed"}</span>
                    </td>
                    <td className={transaction.direction === "credit" ? "transactions-amount-credit" : "transactions-amount-debit"}>
                      {transaction.direction === "credit" ? "+" : "-"}{formatMoney(transaction.amount)}
                    </td>
                    <td>{transaction.balanceBefore === null || transaction.balanceBefore === undefined ? "N/A" : formatMoney(transaction.balanceBefore)}</td>
                    <td>{transaction.balanceAfter === null || transaction.balanceAfter === undefined ? "N/A" : formatMoney(transaction.balanceAfter)}</td>
                    <td>{formatDateTime(transaction.createdAt)}</td>
                    <td>
                      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                        {canApproveManually(transaction) ? (
                          <button
                            type="button"
                            className="row-btn"
                            onClick={() => handleApproveTransaction(transaction)}
                            disabled={approvingId === transaction.id}
                          >
                            {approvingId === transaction.id ? "Approving..." : "Approve"}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="row-btn row-btn-danger"
                          onClick={() => handleDeleteTransaction(transaction.id, transaction.userEmail)}
                          disabled={deletingId === transaction.id}
                        >
                          {deletingId === transaction.id ? "Deleting..." : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="transactions-cards-grid">
          {filteredTransactions.map((transaction) => (
            <article key={`${transaction.id}-card`} className="transactions-mobile-card">
              <div className="transactions-mobile-head">
                <div>
                  <h4>{transaction.userEmail || "Unknown user"}</h4>
                  <p>{TRANSACTION_TYPE_LABELS[transaction.category] || transaction.category || "Transaction"}</p>
                </div>
                <span
                  className={
                    transaction.direction === "credit"
                      ? "transactions-direction-chip transactions-direction-credit"
                      : "transactions-direction-chip transactions-direction-debit"
                  }
                >
                  {transaction.direction || "debit"}
                </span>
              </div>

              <div className="transactions-mobile-grid">
                <p><strong>Description:</strong> {transaction.description || "N/A"}</p>
                <p><strong>Gateway:</strong> {transaction.gatewayLabel || transaction.provider || "N/A"}</p>
                <p><strong>Gateway Ref:</strong> {transaction.reference || "N/A"}</p>
                {transaction.proofImagePath ? (
                  <p>
                    <strong>Proof:</strong>{" "}
                    <a href={resolveProofImageUrl(transaction.proofImagePath)} target="_blank" rel="noreferrer">
                      Open screenshot
                    </a>
                  </p>
                ) : null}
                <p><strong>Status:</strong> {transaction.status || "completed"}</p>
                <p><strong>Amount:</strong> {transaction.direction === "credit" ? "+" : "-"}{formatMoney(transaction.amount)}</p>
                <p><strong>Before:</strong> {transaction.balanceBefore === null || transaction.balanceBefore === undefined ? "N/A" : formatMoney(transaction.balanceBefore)}</p>
                <p><strong>After:</strong> {transaction.balanceAfter === null || transaction.balanceAfter === undefined ? "N/A" : formatMoney(transaction.balanceAfter)}</p>
                <p><strong>Date:</strong> {formatDateTime(transaction.createdAt)}</p>
              </div>

              <div style={{ display: "grid", gap: "10px", marginTop: "10px" }}>
                {canApproveManually(transaction) ? (
                  <button
                    type="button"
                    className="row-btn"
                    onClick={() => handleApproveTransaction(transaction)}
                    disabled={approvingId === transaction.id}
                    style={{ width: "100%" }}
                  >
                    {approvingId === transaction.id ? "Approving..." : "Approve Deposit"}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="row-btn row-btn-danger"
                  onClick={() => handleDeleteTransaction(transaction.id, transaction.userEmail)}
                  disabled={deletingId === transaction.id}
                  style={{ width: "100%" }}
                >
                  {deletingId === transaction.id ? "Deleting..." : "Delete Transaction"}
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>
    </section>
  );
};

export default TransactionsPage;
